/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { action, computed, observable } from 'mobx';
import { CluProjectAssetsSwagger, CluProjectDataSwagger, CluProjectType } from 'src/clu/core/services/projects/cluProjectSwaggers';
import { CluGptPredictionsStore } from 'src/clu/core/stores/CluGptPredictionsStore';
import { CluIntentStore } from 'src/clu/core/stores/CluIntentStore';
import { CluTrainedModelStore } from 'src/clu/core/stores/CluTrainedModelStore';
import { cluEntitySwaggerToCluEntityStore } from 'src/clu/modules/handlers/entities/cluEntityMappers';
import { cluIntentSwaggerToCluIntentStore } from 'src/clu/modules/handlers/intents/cluIntentMappers';
import { AppStore } from 'src/core/stores/app/AppStore';
import { CustomSkillDeploymentStore } from 'src/core/stores/CustomSkillDeploymentStore';
import { ResourceAssignmentStore } from 'src/core/stores/ResourceAssignmentStore';
import { OmitFunctions } from 'src/core/stores/types';
import { EntityStore } from 'src/core/types/EntityStore';
import { createResourceStoreHouse } from 'src/luis/core/stores/resourceStoreHouse';
import { GptJobStore } from 'src/modules/gptPrediction/stores/GptJobStore';
import { EvaluationJobStore } from 'src/modules/modelEvaluation/EvaluationJobInfoStore';
import { TrainingJobStore } from 'src/modules/modelTraining/stores/TrainingJobInfoStore';
import { DatasetType, TrainingServiceStatus } from 'src/modules/modelTraining/types/trainingTypes';

export type CluProjectStoreData = OmitFunctions<Omit<Partial<CluProjectStore>, 'id' | 'projectName'>>;

export class CluProjectStore extends AppStore {
    public projectType: CluProjectType;

    public intentStoreHouse = createResourceStoreHouse<CluIntentStore>();

    public entityStoreHouse = createResourceStoreHouse<EntityStore>();

    public deploymentsStoreHouse = createResourceStoreHouse<CustomSkillDeploymentStore>();

    public trainingJobStoreHouse = createResourceStoreHouse<TrainingJobStore>();

    public trainedModelStoreHouse = createResourceStoreHouse<CluTrainedModelStore>();

    public resourceAssignmentsStoreHouse = createResourceStoreHouse<ResourceAssignmentStore>();

    public readonly gptJobsStoreHouse = createResourceStoreHouse<GptJobStore>();

    public readonly gptPredictionsStore = new CluGptPredictionsStore();

    @observable public oosThreshold: number;

    @observable public isMultiLingual: boolean;

    @observable public isCasingNormalized: boolean;

    @observable public isDiacriticsAugmented: boolean;

    @observable public jsonData: CluProjectAssetsSwagger;

    @observable public evaluationJobStore: EvaluationJobStore;

    constructor(projectName: string, metadata: Partial<CluProjectStoreData>) {
        super(projectName, 'clu', metadata);

        this.name = projectName;
        this.projectType = metadata.projectType;
        this.oosThreshold = metadata.oosThreshold;
        this.isMultiLingual = metadata.isMultiLingual;
        this.isCasingNormalized = metadata.isCasingNormalized;
        this.isDiacriticsAugmented = metadata.isDiacriticsAugmented;
        this.jsonData = { projectKind: metadata.projectType, intents: [], entities: [], utterances: [] };
    }

    @action
    public updateProject(projectSwagger: CluProjectDataSwagger) {
        this.oosThreshold = projectSwagger.metadata.settings?.confidenceThreshold ?? 0;
        this.isMultiLingual = projectSwagger.metadata.multilingual;
        this.isCasingNormalized = projectSwagger.metadata.settings?.normalizeCasing ?? false;
        this.isDiacriticsAugmented = projectSwagger.metadata.settings?.augmentDiacritics ?? false;
        this.description = projectSwagger.metadata.description;
        this.patchJsonData({
            intents: projectSwagger.assets.intents,
            entities: projectSwagger.assets.entities,
            utterances: projectSwagger.assets.utterances
        });

        this.intentStoreHouse.setItems(projectSwagger.assets.intents.map(cluIntentSwaggerToCluIntentStore));
        this.entityStoreHouse.setItems(projectSwagger.assets.entities.map(cluEntitySwaggerToCluEntityStore));
    }

    @computed
    public get labelsPerIntent(): Map<string, number> {
        return this.jsonData.utterances.reduce(
            (mapAcc, e) => (mapAcc.has(e.intent) ? mapAcc.set(e.intent, mapAcc.get(e.intent) + 1) : mapAcc.set(e.intent, 1)),
            new Map<string, number>()
        );
    }

    @computed
    public get labelsPerEntity(): Map<string, number> {
        return this.jsonData.utterances.reduce((mapAcc, u) => {
            u.entities.forEach(e =>
                mapAcc.has(e.category) ? mapAcc.set(e.category, mapAcc.get(e.category) + 1) : mapAcc.set(e.category, 1)
            );

            return mapAcc;
        }, new Map<string, number>());
    }

    @action
    public classifiedLabelsPerEntity(dataset: DatasetType): Map<string, number> {
        return this.jsonData.utterances
            .filter(u => (u.dataset || 'Train') === dataset)
            .reduce((mapAcc, u) => {
                u.entities.forEach(e =>
                    mapAcc.has(e.category) ? mapAcc.set(e.category, mapAcc.get(e.category) + 1) : mapAcc.set(e.category, 1)
                );

                return mapAcc;
            }, new Map<string, number>());
    }

    @computed
    public get entitiesPerIntent(): Map<string, string[]> {
        return this.jsonData.intents.reduce((mapAcc, intent) => {
            const entities = new Set<string>();
            this.jsonData.utterances.forEach(u => u.intent === intent.category && u.entities.forEach(e => entities.add(e.category)));

            return mapAcc.set(intent.category, Array.from(entities));
        }, new Map<string, string[]>());
    }

    @computed
    public get intentsPerEntity(): Map<string, string[]> {
        return this.jsonData.entities.reduce((mapAcc, entity) => {
            const intents = new Set<string>();
            this.jsonData.utterances.forEach(
                u => u.entities.find(e => e.category === entity.category) !== undefined && intents.add(u.intent)
            );

            return mapAcc.set(entity.category, Array.from(intents));
        }, new Map<string, string[]>());
    }

    /**
     * Gets the model that is currently being trained.
     */
    @computed
    public get currentTrainingJob(): TrainingJobStore {
        return this.trainingJobStoreHouse.items.find(t => t.status === 'inProgress');
    }

    /**
     * Gets whether a model is being trained or not.
     */
    @computed
    public get trainingServiceStatus(): TrainingServiceStatus {
        if (this.currentTrainingJob) {
            return 'inProgress';
        }

        return this.trainingJobStoreHouse.items.find(t => t.status === 'notStarted' || t.status === 'cancelling') ? 'queued' : 'ready';
    }

    /**
     * Gets the percentage of utterances in the training set defined by the user
     */
    @computed
    public get trainingSetPercentage(): number {
        return Math.ceil(
            (this.jsonData.utterances.filter(u => (u.dataset || 'Train') === 'Train').length * 100) / this.jsonData.utterances.length
        );
    }

    /**
     * Gets the percentage of utterances in the testing set defined by the user
     */
    @computed
    public get testingSetPercentage(): number {
        return Math.floor((this.jsonData.utterances.filter(u => u.dataset === 'Test').length * 100) / this.jsonData.utterances.length);
    }

    @action
    public setOosThreshold(oosThreshold: number) {
        this.oosThreshold = oosThreshold;
    }

    @action
    public setIsMultiLingual(value: boolean) {
        this.isMultiLingual = value;
    }

    @action
    public setIsCasingNormalized(value: boolean) {
        this.isCasingNormalized = value;
    }

    @action
    public setIsDiacriticsAugmented(value: boolean) {
        this.isDiacriticsAugmented = value;
    }

    @action
    public patchJsonData(data: Partial<CluProjectAssetsSwagger>) {
        this.jsonData = { ...this.jsonData, ...data };
    }

    @action
    public setEvaluationJobStore(val: EvaluationJobStore): void {
        this.evaluationJobStore = val;
    }
}
